import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { canAnimate, duration } from "@/shared/lib";
import { tactile } from "@/shared/telegram";

import { OverlayContext } from "./overlay-context";
import { pushOverlay } from "./overlay-stack";

/**
 * Общая механика оверлеев: затемнение, блокировка прокрутки под ним, закрытие
 * по Escape и по клику вне, регистрация в стеке для кнопки «назад».
 *
 * Затемнений в макете два разных: 0.72 под нижним шитом фильтров (28:45) и
 * 0.78 под модальным подтверждением (28:67). Модалка перекрывает сильнее,
 * потому что требует осознанного решения — это не случайность, поэтому
 * значения не сведены к одному.
 *
 * Отклик (FE-14): появление и уход поверхности отзываются `surface` — самым
 * жёстким из импульсов. Оверлей перекрывает экран целиком, и без тактильной
 * отбивки его приход читается как подмена содержимого, а не как слой сверху.
 *
 * Слой (FE-13): оверлей уходит порталом в `document.body`, а не остаётся там,
 * где его смонтировал экран. Экраны живут внутри `<main>` со своим `z-index`,
 * а это отдельный контекст наложения: `z-50` оверлея сравнивался бы не с
 * панелью навигации, а с содержимым `<main>`, и панель (`z-10`, но в корневом
 * контексте) рисовалась бы поверх шита — перекрывая его нижнюю кнопку.
 * Портал ставит оверлей в один контекст с панелью, где `z-50` наконец значит
 * то, что написано.
 *
 * Движение (FE-13): закрытие перехвачено. Экран монтирует оверлей условно, и
 * если сразу дёрнуть его `onClose`, элемент исчезнет из дерева до всякой
 * анимации. Поэтому запрос на закрытие сначала проигрывает уход (затемнение
 * гаснет, содержимое уезжает по своей траектории) и только потом сообщает
 * экрану. Содержимое узнаёт об уходе через `data-state="closing"` на корне —
 * им же пользуются варианты `Sheet` и `Modal`.
 */
interface OverlayProps {
  onClose: () => void;
  strong?: boolean;
  children: ReactNode;
  labelledBy?: string;
}

export function Overlay({ onClose, strong = false, children, labelledBy }: OverlayProps) {
  const [closing, setClosing] = useState(false);

  const requestClose = useCallback(() => {
    if (closing) return;
    tactile("surface");

    // Без анимации ждать нечего: и в тест-окружении, и при уменьшенном
    // движении задержка была бы просто подвисанием интерфейса.
    if (!canAnimate()) {
      onClose();
      return;
    }

    setClosing(true);
    window.setTimeout(onClose, duration("base"));
  }, [closing, onClose]);

  useEffect(() => {
    tactile("surface");
  }, []);

  useEffect(() => {
    const pop = pushOverlay(requestClose);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestClose();
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      pop();
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [requestClose]);

  const api = useMemo(() => ({ requestClose, closing }), [requestClose, closing]);

  return createPortal(
    <OverlayContext value={api}>
      <div
        data-state={closing ? "closing" : "open"}
        className={[
          "fixed inset-0 z-50 flex",
          strong ? "bg-scrim-strong" : "bg-scrim",
          "animate-fade-in data-[state=closing]:animate-fade-out",
        ].join(" ")}
        onClick={requestClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          {...(labelledBy ? { "aria-labelledby": labelledBy } : {})}
          className="contents"
          onClick={(event) => {
            // Клик внутри не должен закрывать: закрывает только фон.
            event.stopPropagation();
          }}
        >
          {children}
        </div>
      </div>
    </OverlayContext>,
    document.body,
  );
}
